import {Accordion, AccordionDetails, AccordionSummary, Card, Grid} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {SwitchSegmentedYesNo, TextBox} from '../../../Core/UI';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import {SelectInputElement} from '../Components/SelectInputElement';
import React from 'react';
import {observer} from 'mobx-react-lite';
import {HealthQuestionsProps} from './HealthQuestionsList';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/HealthXref';

const DigestiveSystemDisorder = observer(({counter, valueChanged, parameters, coreQuestions}: HealthQuestionsProps) => {
    return (
        <Accordion style={{width: '100%'}} TransitionProps={{unmountOnExit: true}}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={'digestiveSystemDisorder-content'}
                id={'digestiveSystemDisorder-header'}
            >
                <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    <div>Digestive system disorder</div>
                    <div>{counter('digestiveSystemDisorder')}</div>
                </div>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container>
                    <Grid key="params" container>
                        {coreQuestions?.map(({name, label}) => (
                            <Grid item xs={4} key={name}>
                                <SwitchSegmentedYesNo
                                    param={{
                                        name: name,
                                        label: label,
                                        category: 'digestiveSystemDisorder',
                                        ...(parameters[name as keyof typeof parameters] as Parameter),
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                />
                            </Grid>
                        ))}
                        {parameters.IBSIrritableBowelSyndrome.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>IBS (Irritable Bowel Syndrome)</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.ibsDiagnosisDate?.value || null}
                                                    onChange={(date) => valueChanged(parameters.ibsDiagnosisDate, date)}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'ibsCondition',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results, referral to hospital, surgery?',
                                                    ...parameters.ibsCondition,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ibsTreatment"
                                                label="Provide details on all pending evaluations and treatment related to this condition."
                                                value={parameters.ibsTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.ibsTreatment, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.CrohnsDisease.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Crohn's Disease</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'chronsdiseaseColonscopy',
                                                    label: 'Have you had a colonoscopy in the last 2 years?',
                                                    ...parameters.chronsdiseaseColonscopy,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="crohnsdiseaseTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.crohnsdiseaseTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.crohnsdiseaseTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'chronsdiseaseSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.chronsdiseaseSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Ulcers.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Ulcers</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.ulcerDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.ulcerDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'ulcerEpisodes',
                                                    label: 'Have you had more than one episode of this condition?',
                                                    ...parameters.ulcerEpisodes,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ulcerTreatment"
                                                label="Provide details on treatment received and dates of diagnosis (mm/yy)"
                                                value={parameters.ulcerTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.ulcerTreatment, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ulcerSurgery3"
                                                label="Have you had surgery to treat this condition?"
                                                value={parameters.ulcerSurgery3?.value}
                                                onChange={(value) => valueChanged(parameters.ulcerSurgery3, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ulcerSurgery2"
                                                label="Provide details to include date of surgery (mm/yy) and any other treatment received."
                                                value={parameters.ulcerSurgery2?.value}
                                                onChange={(value) => valueChanged(parameters.ulcerSurgery2, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ulcerSurgery1"
                                                label="Have you had an endoscopy and biopsy for this condition?"
                                                value={parameters.ulcerSurgery1?.value}
                                                onChange={(value) => valueChanged(parameters.ulcerSurgery1, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Were the biopsy results?',
                                                    name: 'ulcerSurgery',
                                                    options: fieldOptions.ulcerSurgery.options,
                                                    ...parameters.ulcerSurgery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.UlcerativeColitis.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ulcerativecolitisDiagnosisYears"
                                                label="How long ago were you diagnosed?"
                                                value={parameters.ulcerativecolitisDiagnosisYears?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.ulcerativecolitisDiagnosisYears, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'coloscopyCompletedRecently',
                                                    label: 'Have you had a colonoscopy in the last 2 years?',
                                                    ...parameters.coloscopyCompletedRecently,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'ulcerativecolitisSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.ulcerativecolitisSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ulcerativecolitisTreatment"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.ulcerativecolitisTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.ulcerativecolitisTreatment, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.GERDHeartburn.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.gerdDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.gerdDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'gerdComplications',
                                                    label: 'Have you had any complications associated with this condition?',
                                                    ...parameters.gerdComplications,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'gerdBiopsyCompleted',
                                                    label: 'Have you had an endoscopy and biopsy for this condition?',
                                                    ...parameters.gerdBiopsyCompleted,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Were the biopsy results?',
                                                    name: 'gerdBiopsyResults',
                                                    options: fieldOptions.gerdBiopsyResults.options,
                                                    ...parameters.gerdBiopsyResults,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="gerdTreatmentPlan"
                                                label="Provide details to include treatment received and date of diagnosis."
                                                value={parameters.gerdTreatmentPlan?.value}
                                                onChange={(value) => valueChanged(parameters.gerdTreatmentPlan, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
});

export default DigestiveSystemDisorder;
